import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT, COLORS } from '../utils/Constants';

const RULES_TEXT = `
ITALIAN BURACO - RULES

OVERVIEW
Buraco (Burraco) is an Italian rummy-type card game in the Canasta family.
The goal is to form melds (combinations of cards) and score points.
The first player to reach 2005 points wins.

SETUP
- Uses 2 standard 52-card decks plus 4 jokers (108 cards total)
- Each player is dealt 11 cards
- Two face-down piles of 11 cards (pozzetti) are placed on the table
- Remaining cards form the draw pile; one card is turned face-up for the discard pile

TURN STRUCTURE
1. DRAW: Take 1 card from the draw pile, OR take the entire discard pile
2. MELD (optional): Play new melds or add cards to your existing melds
3. DISCARD: Place 1 card face-up on the discard pile

MELDS
Sets (Groups): 3 or more cards of the same rank
  - Maximum 1 wild card per set
  - Maximum 9 cards per set

Runs (Sequences): 3 or more consecutive cards of the same suit
  - Maximum 1 wild card per run
  - Ace can be low (A-2-3) or high (Q-K-A) but not both
  - A natural 2 in its correct suit position counts as natural, not wild

WILD CARDS
Jokers and 2s are wild cards. Each meld may contain at most one wild card.
Exception: A 2 placed in its natural position (rank 2 of matching suit) is not wild.

BURRACO (7+ Card Meld)
Clean Burraco (Pulita): No wild cards - 200 point bonus
Dirty Burraco (Sporca): Contains a wild card - 100 point bonus

THE POZZETTO
When you empty your hand, you pick up a pozzetto (11 new cards):
- If you melded all cards (no discard): pick up immediately and continue your turn ("in flight")
- If you discarded your last card: pick up the pozzetto but wait until your next turn
After one player takes a pozzetto, the remaining one is reserved for the opponent.

GOING OUT (CHIUSURA)
To end the round, you must:
- Have already taken your pozzetto
- Have at least one Burraco among your melds
- End with a discard (you cannot meld all cards)
- Your final discard cannot be a wild card (Joker or 2)

OTHER WAYS THE ROUND ENDS
- Draw pile reduced to 2 cards (round ends after the player who drew the 3rd-to-last card)
- Stalemate: 4 consecutive turns where each player only draws the opponent's single discard

SCORING

Score = (meld card values) - (hand card values) + bonuses

WINNING
The game continues across multiple rounds until one player exceeds 2005 points.
The player with the highest score wins.
`.trim();

export class RulesScene extends Phaser.Scene {
  private scrollY = 0;
  private maxScroll = 0;
  private rulesContainer!: Phaser.GameObjects.Container;
  private returnTo = 'MenuScene';

  constructor() {
    super({ key: 'RulesScene' });
  }

  init(data: { returnTo?: string }): void {
    this.returnTo = data.returnTo || 'MenuScene';
    this.scrollY = 0;
  }

  private renderScoringTables(startY: number): number {
    let y = startY;
    const leftCol = 60;
    const rightCol = 340;
    const rowH = 26;
    const tableW = 440;
    const headerStyle = { fontSize: '15px', color: '#f1c40f', fontFamily: 'Georgia, serif', fontStyle: 'bold' };
    const cellStyle = { fontSize: '14px', color: '#cccccc', fontFamily: 'Georgia, serif' };
    const valueStyle = { fontSize: '14px', color: '#ffffff', fontFamily: 'Georgia, serif', fontStyle: 'bold' };

    // --- Card Values Table ---
    const cardLabel = this.add.text(leftCol, y, 'Card Values', headerStyle);
    this.rulesContainer.add(cardLabel);
    y += rowH + 2;

    // Header row background
    const headerBg = this.add.rectangle(leftCol + tableW / 2 - 20, y + rowH / 2, tableW, rowH, 0xffffff, 0.08);
    this.rulesContainer.add(headerBg);
    const colHead1 = this.add.text(leftCol, y + 4, 'Card', { ...cellStyle, fontStyle: 'bold' } as Phaser.Types.GameObjects.Text.TextStyle);
    const colHead2 = this.add.text(rightCol, y + 4, 'Points', { ...cellStyle, fontStyle: 'bold' } as Phaser.Types.GameObjects.Text.TextStyle);
    this.rulesContainer.add(colHead1);
    this.rulesContainer.add(colHead2);
    y += rowH;

    const cardRows = [
      ['Joker', '30'],
      ['2 (as wild)', '20'],
      ['Ace', '15'],
      ['K, Q, J, 10, 9, 8', '10'],
      ['7, 6, 5, 4, 3', '5'],
    ];

    for (let i = 0; i < cardRows.length; i++) {
      // Alternating row background
      if (i % 2 === 0) {
        const rowBg = this.add.rectangle(leftCol + tableW / 2 - 20, y + rowH / 2, tableW, rowH, 0xffffff, 0.04);
        this.rulesContainer.add(rowBg);
      }
      const label = this.add.text(leftCol, y + 4, cardRows[i][0], cellStyle as Phaser.Types.GameObjects.Text.TextStyle);
      const value = this.add.text(rightCol, y + 4, cardRows[i][1], valueStyle as Phaser.Types.GameObjects.Text.TextStyle);
      this.rulesContainer.add(label);
      this.rulesContainer.add(value);
      y += rowH;
    }

    y += 14;

    // --- Bonuses Table ---
    const bonusLabel = this.add.text(leftCol, y, 'Bonuses & Penalties', headerStyle);
    this.rulesContainer.add(bonusLabel);
    y += rowH + 2;

    const headerBg2 = this.add.rectangle(leftCol + tableW / 2 - 20, y + rowH / 2, tableW, rowH, 0xffffff, 0.08);
    this.rulesContainer.add(headerBg2);
    const colHead3 = this.add.text(leftCol, y + 4, 'Bonus', { ...cellStyle, fontStyle: 'bold' } as Phaser.Types.GameObjects.Text.TextStyle);
    const colHead4 = this.add.text(rightCol, y + 4, 'Points', { ...cellStyle, fontStyle: 'bold' } as Phaser.Types.GameObjects.Text.TextStyle);
    this.rulesContainer.add(colHead3);
    this.rulesContainer.add(colHead4);
    y += rowH;

    const bonusRows = [
      ['Clean Burraco (Pulita)', '+200', '#2ecc71'],
      ['Dirty Burraco (Sporca)', '+100', '#e67e22'],
      ['Going Out', '+100', '#2ecc71'],
      ['No Pozzetto', '-100', '#e74c3c'],
    ];

    for (let i = 0; i < bonusRows.length; i++) {
      if (i % 2 === 0) {
        const rowBg = this.add.rectangle(leftCol + tableW / 2 - 20, y + rowH / 2, tableW, rowH, 0xffffff, 0.04);
        this.rulesContainer.add(rowBg);
      }
      const label = this.add.text(leftCol, y + 4, bonusRows[i][0], cellStyle as Phaser.Types.GameObjects.Text.TextStyle);
      const value = this.add.text(rightCol, y + 4, bonusRows[i][1], {
        fontSize: '14px', color: bonusRows[i][2], fontFamily: 'Georgia, serif', fontStyle: 'bold',
      });
      this.rulesContainer.add(label);
      this.rulesContainer.add(value);
      y += rowH;
    }

    y += 10;
    return y;
  }

  create(): void {
    // Background
    this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, COLORS.UI_BG);

    // Title bar
    this.add.rectangle(GAME_WIDTH / 2, 30, GAME_WIDTH, 60, COLORS.TABLE_FELT);
    this.add.text(GAME_WIDTH / 2, 30, 'RULES', {
      fontSize: '28px',
      color: COLORS.TEXT_LIGHT,
      fontFamily: 'Georgia, serif',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // Back button
    const backBtn = this.add.text(30, 30, '< Back', {
      fontSize: '20px',
      color: '#ffcc00',
      fontFamily: 'Georgia, serif',
    }).setOrigin(0, 0.5)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => {
        if (this.returnTo === 'GameScene') {
          this.scene.stop();
          this.scene.resume('GameScene');
        } else {
          this.scene.start(this.returnTo);
        }
      })
      .on('pointerover', () => backBtn.setColor('#ffffff'))
      .on('pointerout', () => backBtn.setColor('#ffcc00'));

    // Rules text container (scrollable)
    this.rulesContainer = this.add.container(0, 70);

    const lines = RULES_TEXT.split('\n');
    let yPos = 10;

    for (const line of lines) {
      const isHeader = line === line.toUpperCase() && line.trim().length > 0 && !line.startsWith(' ');
      const fontSize = isHeader ? '18px' : '15px';
      const color = isHeader ? '#f1c40f' : '#cccccc';
      const fontStyle = isHeader ? 'bold' : 'normal';

      const text = this.add.text(40, yPos, line, {
        fontSize,
        color,
        fontFamily: 'Georgia, serif',
        fontStyle,
        wordWrap: { width: GAME_WIDTH - 80 },
      });

      this.rulesContainer.add(text);
      yPos += text.height + 4;

      // Insert scoring tables after the SCORING header
      if (line.trim() === 'SCORING') {
        yPos += 6;
        yPos = this.renderScoringTables(yPos);
      }
    }

    this.maxScroll = Math.max(0, yPos - (GAME_HEIGHT - 80));

    // Scroll mask
    const mask = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 30, GAME_WIDTH, GAME_HEIGHT - 60, 0x000000);
    mask.setVisible(false);
    this.rulesContainer.setMask(mask.createGeometryMask());

    // Mouse wheel scrolling
    this.input.on('wheel', (_pointer: Phaser.Input.Pointer, _gameObjects: Phaser.GameObjects.GameObject[], _deltaX: number, deltaY: number) => {
      this.scrollY = Phaser.Math.Clamp(this.scrollY + deltaY * 0.5, 0, this.maxScroll);
      this.rulesContainer.y = 70 - this.scrollY;
    });

    // Scroll indicator
    if (this.maxScroll > 0) {
      this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 20, 'Scroll to read more', {
        fontSize: '14px',
        color: '#888888',
        fontFamily: 'Georgia, serif',
        fontStyle: 'italic',
      }).setOrigin(0.5);
    }
  }
}
